import React, { useState, useEffect } from 'react';
import DashboardLayout from './DashboardLayout';
import { getDashboardStats, getDailyReport, getDemoDataCount, deleteDemoData, getBranches, getWeeklySales } from '../lib/api';
import { formatCurrency, getStatusLabel } from '../lib/utils';
import { 
  DollarSign, 
  Car, 
  Star, 
  Gift,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Calendar,
  Trash2,
  AlertTriangle,
  Building2,
  CalendarRange
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);
  const [demoCount, setDemoCount] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('all'); // 'all' or branch id
  const [weekly, setWeekly] = useState(null);

  useEffect(() => {
    fetchBranches();
    fetchDemoCount();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
    fetchWeekly();
  }, [selectedBranch, reportDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBranches = async () => {
    try {
      const res = await getBranches();
      setBranches(res.data || []);
    } catch { /* silent */ }
  };

  const fetchWeekly = async () => {
    try {
      const branchId = selectedBranch === 'all' ? null : selectedBranch;
      const res = await getWeeklySales(branchId);
      setWeekly(res.data);
    } catch { /* silent */ }
  };

  const fetchData = async () => {
    setLoading(true);
    const branchId = selectedBranch === 'all' ? null : selectedBranch;
    try {
      const [statsRes, reportRes] = await Promise.all([
        getDashboardStats(branchId),
        getDailyReport(reportDate, branchId)
      ]);
      setStats(statsRes.data);
      setReport(reportRes.data);
    } catch (error) {

    }
    setLoading(false);
  };

  const fetchDemoCount = async () => {
    try {
      const res = await getDemoDataCount();
      setDemoCount(res.data);
    } catch (error) {

    }
  };

  const handleDeleteDemo = async () => {
    setDeleting(true);
    try {
      const res = await deleteDemoData();
      toast.success(res.data.message);
      setShowDeleteConfirm(false);
      fetchDemoCount();
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar datos demo');
    }
    setDeleting(false);
  };

  const fetchReport = (date) => {
    setReportDate(date);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fadeIn">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
            <p className="text-slate-600 mt-1">
              Resumen de operaciones del día
              {selectedBranch !== 'all' && (
                <span className="ml-2 text-blue-600 font-medium">
                  · {branches.find(b => b.id === selectedBranch)?.name}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-slate-500" />
              <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                <SelectTrigger className="w-52" data-testid="dashboard-branch-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="branch-option-all">Todas las sucursales</SelectItem>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id} data-testid={`branch-option-${b.id}`}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-slate-400" />
              <span className="text-lg font-medium text-slate-700">
                {new Date().toLocaleDateString('es-MX', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          <StatCard
            title="Ventas del Día"
            value={formatCurrency(stats?.total_sales || 0)}
            icon={DollarSign}
            color="blue"
            testId="stat-sales"
          />
          <StatCard
            title="Vehículos Atendidos"
            value={stats?.vehicles_attended || 0}
            icon={Car}
            color="emerald"
            testId="stat-vehicles"
          />
          <StatCard
            title="Puntos Generados"
            value={stats?.points_earned || 0}
            icon={Star}
            color="amber"
            testId="stat-points-earned"
          />
          <StatCard
            title="Puntos Redimidos"
            value={stats?.points_redeemed || 0}
            icon={Gift}
            color="purple"
            testId="stat-points-redeemed"
          />
        </div>

        {/* Weekly sales (lunes-domingo, hora CDMX) */}
        <WeeklySalesCard data={weekly} />

        {/* Orders Status & Top Services */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Orders by Status */}
          <div className="card-base p-6">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">Estado de Órdenes</h2>
            <div className="space-y-3">
              <StatusRow
                label="Recibido"
                count={stats?.orders_by_status?.recibido || 0}
                color="amber"
                icon={Clock}
              />
              <StatusRow
                label="En Proceso"
                count={stats?.orders_by_status?.en_proceso || 0}
                color="blue"
                icon={TrendingUp}
              />
              <StatusRow
                label="Listo"
                count={stats?.orders_by_status?.listo || 0}
                color="emerald"
                icon={CheckCircle}
              />
              <StatusRow
                label="Entregado"
                count={stats?.orders_by_status?.entregado || 0}
                color="slate"
                icon={CheckCircle}
              />
            </div>
            {(stats?.pending_orders || 0) > 0 && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <span className="text-amber-800 font-medium">
                  {stats.pending_orders} órdenes pendientes de procesar
                </span>
              </div>
            )}
          </div>

          {/* Top Services */}
          <div className="card-base p-6">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">Servicios Más Vendidos</h2>
            {stats?.top_services?.length > 0 ? (
              <div className="space-y-3">
                {stats.top_services.map((service, index) => (
                  <div 
                    key={service.name}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 flex items-center justify-center bg-blue-100 text-blue-600 font-bold rounded-full text-sm">
                        {index + 1}
                      </span>
                      <span className="font-medium text-slate-700">{service.name}</span>
                    </div>
                    <span className="text-lg font-semibold text-slate-900">{service.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No hay ventas registradas hoy</p>
            )}
          </div>
        </div>

        {/* Daily Report */}
        <div className="card-base p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <h2 className="text-xl font-semibold text-slate-900">Corte de Caja</h2>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={reportDate}
                onChange={(e) => {
                  setReportDate(e.target.value);
                  fetchReport(e.target.value);
                }}
                className="w-auto"
                data-testid="report-date-input"
              />
            </div>
          </div>

          {report && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 bg-blue-50 rounded-xl">
                  <div className="text-sm text-blue-600 font-medium">Total Órdenes</div>
                  <div className="text-2xl font-bold text-blue-900">{report.total_orders}</div>
                </div>
                <div className="p-4 bg-emerald-50 rounded-xl">
                  <div className="text-sm text-emerald-600 font-medium">Total Ventas</div>
                  <div className="text-2xl font-bold text-emerald-900">{formatCurrency(report.total_sales)}</div>
                </div>
                <div className="p-4 bg-slate-100 rounded-xl">
                  <div className="text-sm text-slate-600 font-medium">Fecha</div>
                  <div className="text-2xl font-bold text-slate-900">{report.date}</div>
                </div>
              </div>

              {/* By Payment Method */}
              <div>
                <h3 className="text-lg font-medium text-slate-800 mb-3">Por Método de Pago</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="p-4 border border-slate-200 rounded-xl">
                    <div className="text-sm text-slate-500">Efectivo</div>
                    <div className="text-xl font-bold text-slate-900">{formatCurrency(report.by_payment_method?.efectivo || 0)}</div>
                  </div>
                  <div className="p-4 border border-slate-200 rounded-xl">
                    <div className="text-sm text-slate-500">Tarjeta</div>
                    <div className="text-xl font-bold text-slate-900">{formatCurrency(report.by_payment_method?.tarjeta || 0)}</div>
                  </div>
                  <div className="p-4 border border-slate-200 rounded-xl">
                    <div className="text-sm text-slate-500">Transferencia</div>
                    <div className="text-xl font-bold text-slate-900">{formatCurrency(report.by_payment_method?.transferencia || 0)}</div>
                  </div>
                </div>
              </div>

              {/* Orders List */}
              {report.orders?.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-slate-800 mb-3">Detalle de Órdenes</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="table-header">Folio</th>
                          <th className="table-header">Servicios</th>
                          <th className="table-header">Sucursal</th>
                          <th className="table-header">Cajero</th>
                          <th className="table-header">Método</th>
                          <th className="table-header text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.orders.map((order) => (
                          <tr key={order.folio} className="table-row">
                            <td className="table-cell font-mono text-blue-600">{order.folio}</td>
                            <td className="table-cell">{order.services.join(', ')}</td>
                            <td className="table-cell text-slate-700">{order.branch_name || '—'}</td>
                            <td className="table-cell text-slate-700">{order.cashier_name || '—'}</td>
                            <td className="table-cell capitalize">{order.payment_method}</td>
                            <td className="table-cell text-right font-semibold">{formatCurrency(order.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Demo Data Management */}
        {demoCount && demoCount.total > 0 && (
          <div className="card-base p-6 border-2 border-amber-200 bg-amber-50/50" data-testid="demo-data-section">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-100 rounded-xl">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-slate-900 mb-1">Datos de Demostración</h2>
                <p className="text-slate-600 text-sm mb-4">
                  El sistema contiene datos demo para pruebas. Elimínalos antes del deploy a producción.
                </p>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
                  {[
                    { label: 'Usuarios', count: demoCount.users },
                    { label: 'Clientes', count: demoCount.clients },
                    { label: 'Servicios', count: demoCount.services },
                    { label: 'Recompensas', count: demoCount.rewards },
                    { label: 'Órdenes', count: demoCount.orders },
                    { label: 'Citas', count: demoCount.appointments },
                  ].map(item => (
                    <div key={item.label} className="p-3 bg-white rounded-lg border border-amber-200 text-center">
                      <div className="text-2xl font-bold text-amber-700">{item.count}</div>
                      <div className="text-xs text-slate-500">{item.label}</div>
                    </div>
                  ))}
                </div>

                {!showDeleteConfirm ? (
                  <Button 
                    variant="destructive" 
                    onClick={() => setShowDeleteConfirm(true)}
                    data-testid="delete-demo-btn"
                    className="gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Eliminar {demoCount.total} registros demo
                  </Button>
                ) : (
                  <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                    <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
                    <p className="text-sm text-red-800 font-medium">
                      Esta acción es irreversible. Se borrarán todos los datos marcados como demo.
                    </p>
                    <div className="flex gap-2 shrink-0">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setShowDeleteConfirm(false)}
                        disabled={deleting}
                        data-testid="cancel-delete-demo-btn"
                      >
                        Cancelar
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={handleDeleteDemo}
                        disabled={deleting}
                        data-testid="confirm-delete-demo-btn"
                        className="gap-2"
                      >
                        {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        Confirmar Eliminación
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function StatCard({ title, value, icon: Icon, color, testId }) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    amber: 'bg-amber-100 text-amber-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  return (
    <div className="stat-card" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-500 font-medium">{title}</p>
          <p className="text-3xl font-bold text-slate-900 mt-2">{value}</p>
        </div>
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
}

function StatusRow({ label, count, color, icon: Icon }) {
  const colorClasses = {
    amber: 'bg-amber-100 text-amber-600',
    blue: 'bg-blue-100 text-blue-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    slate: 'bg-slate-100 text-slate-600',
  };

  return (
    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <span className="font-medium text-slate-700">{label}</span>
      </div>
      <span className="text-xl font-bold text-slate-900">{count}</span>
    </div>
  );
}



function WeeklySalesCard({ data }) {
  if (!data) {
    return (
      <div className="card-base p-6 flex items-center justify-center" data-testid="weekly-sales-card-loading">
        <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
      </div>
    );
  }
  const max = Math.max(...data.days.map(d => d.total), 1);

  const formatRange = () => {
    try {
      const s = new Date(data.week_start + 'T12:00:00');
      const e = new Date(data.week_end + 'T12:00:00');
      const fmt = (d) => d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
      return `${fmt(s)} – ${fmt(e)}`;
    } catch { return ''; }
  };

  return (
    <div className="card-base p-6" data-testid="weekly-sales-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <CalendarRange className="w-5 h-5 text-blue-600" />
            Ingresos de la Semana
          </h2>
          <p className="text-xs text-slate-500 mt-1">{formatRange()} · Reinicia cada lunes</p>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-500 uppercase font-semibold">Total semana</div>
          <div className="text-2xl font-bold text-blue-700 tabular-nums" data-testid="weekly-sales-total">
            {formatCurrency(data.total)}
          </div>
          <div className="text-xs text-slate-500">{data.orders_count} ventas</div>
        </div>
      </div>

      <div className="space-y-2">
        {data.days.map((d) => {
          const pct = max > 0 ? Math.round((d.total / max) * 100) : 0;
          return (
            <div key={d.date} className={`flex items-center gap-3 ${d.is_today ? 'bg-blue-50 -mx-2 px-2 py-1 rounded' : ''}`} data-testid={`weekly-day-${d.date}`}>
              <div className="w-24 shrink-0">
                <div className="font-medium text-slate-800 text-sm">{d.day_name}</div>
                <div className="text-xs text-slate-400">{d.date.slice(5)}</div>
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-500">{d.orders} {d.orders === 1 ? 'venta' : 'ventas'}</span>
                  <span className="text-sm font-semibold text-slate-900 tabular-nums">{formatCurrency(d.total)}</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div className={`h-2 rounded-full transition-all ${d.is_today ? 'bg-blue-500' : 'bg-blue-300'}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-3 gap-2 text-center text-sm">
        <div>
          <div className="text-xs text-slate-500">Efectivo</div>
          <div className="font-semibold text-emerald-600 tabular-nums">{formatCurrency(data.by_method?.efectivo || 0)}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">Tarjeta</div>
          <div className="font-semibold text-blue-600 tabular-nums">{formatCurrency(data.by_method?.tarjeta || 0)}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">Transferencia</div>
          <div className="font-semibold text-violet-600 tabular-nums">{formatCurrency(data.by_method?.transferencia || 0)}</div>
        </div>
      </div>
    </div>
  );
}
